import GetAppIcon from '@mui/icons-material/GetApp'
import IconButton from '@mui/material/IconButton'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { detectInstallEnvironment } from 'handlers/detectInstallEnvironment'
import { getPwaInstallGuide } from 'handlers/getPwaInstallGuide'
import { OPEN_PWA_INSTALL_GUIDE_EVENT } from 'handlers/openPwaInstallGuide'

import InstallPwaGuideModal from './InstallPwaGuideModal'
import {
  InstallPwaBannerCloseButton,
  InstallPwaBannerInstallButton,
  InstallPwaBannerRoot,
  InstallPwaBannerText,
} from './InstallPwaBanner.styles'

const BANNER_DISMISS_KEY = 'admin-pwa-install-banner-dismissed'
const MODAL_AUTO_SHOWN_KEY = 'admin-pwa-install-guide-auto-shown'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/**
 * Хост: баннер + модалка с инструкцией под ОС/браузер + реакция на событие открытия.
 */
const InstallPwaGuideHost: FC = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    if (typeof sessionStorage === 'undefined') {
      return false
    }
    return sessionStorage.getItem(BANNER_DISMISS_KEY) === '1'
  })

  const environment = useMemo(() => detectInstallEnvironment(), [])
  const guide = useMemo(
    () => getPwaInstallGuide(environment, Boolean(deferredPrompt)),
    [environment, deferredPrompt]
  )

  const openModal = useCallback((): void => {
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback((): void => {
    setIsModalOpen(false)
  }, [])

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event): void => {
      event.preventDefault()
      setDeferredPrompt(event as BeforeInstallPromptEvent)
    }

    const handleOpenGuideEvent = (): void => {
      openModal()
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener(
      OPEN_PWA_INSTALL_GUIDE_EVENT,
      handleOpenGuideEvent
    )

    // Один раз за сессию показать подсказку, если ещё не установлено
    if (
      !environment.isStandalone &&
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem(MODAL_AUTO_SHOWN_KEY) !== '1'
    ) {
      sessionStorage.setItem(MODAL_AUTO_SHOWN_KEY, '1')
      setIsModalOpen(true)
    }

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      )
      window.removeEventListener(
        OPEN_PWA_INSTALL_GUIDE_EVENT,
        handleOpenGuideEvent
      )
    }
  }, [environment.isStandalone, openModal])

  /**
   * Скрывает компактный баннер до конца сессии.
   */
  const dismissBanner = (): void => {
    sessionStorage.setItem(BANNER_DISMISS_KEY, '1')
    setBannerDismissed(true)
  }

  /**
   * Системный диалог установки (Chrome/Edge), если событие сохранено.
   */
  const handleNativeInstall = async (): Promise<void> => {
    if (!deferredPrompt) {
      return
    }
    await deferredPrompt.prompt()
    await deferredPrompt.userChoice
    setDeferredPrompt(null)
    closeModal()
    dismissBanner()
  }

  const showBanner =
    !bannerDismissed &&
    !environment.isStandalone &&
    (Boolean(deferredPrompt) ||
      environment.operatingSystem === 'ios' ||
      environment.operatingSystem === 'android' ||
      environment.operatingSystem === 'windows' ||
      environment.operatingSystem === 'macos')

  return (
    <>
      {showBanner ? (
        <InstallPwaBannerRoot role='region' aria-label='Установка приложения'>
          <InstallPwaBannerText>
            Установите панель как приложение — удобнее принимать заказы и
            получать уведомления. Инструкция для {guide.environmentLabel}.
          </InstallPwaBannerText>
          <InstallPwaBannerInstallButton type='button' onClick={openModal}>
            Как установить
          </InstallPwaBannerInstallButton>
          {deferredPrompt ? (
            <InstallPwaBannerInstallButton
              type='button'
              onClick={() => void handleNativeInstall()}
            >
              Установить
            </InstallPwaBannerInstallButton>
          ) : null}
          <InstallPwaBannerCloseButton type='button' onClick={dismissBanner}>
            Позже
          </InstallPwaBannerCloseButton>
        </InstallPwaBannerRoot>
      ) : null}

      <InstallPwaGuideModal
        open={isModalOpen}
        guide={guide}
        canUseNativeInstall={Boolean(deferredPrompt)}
        onClose={closeModal}
        onNativeInstall={() => void handleNativeInstall()}
      />
    </>
  )
}

/**
 * Кнопка в шапке: открыть инструкцию установки.
 */
export const OpenPwaInstallGuideButton: FC = () => {
  return (
    <IconButton
      color='inherit'
      aria-label='Как установить приложение'
      title='Как установить приложение'
      onClick={() => {
        window.dispatchEvent(new CustomEvent(OPEN_PWA_INSTALL_GUIDE_EVENT))
      }}
    >
      <GetAppIcon />
    </IconButton>
  )
}

export default InstallPwaGuideHost
